import React from 'react';

interface SidePanelProps {
  /** Which edge the panel is docked to; decides border and collapse-arrow direction. */
  side: 'left' | 'right';
  title: string;
  /** Optional item count shown next to the title. */
  count?: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Current expanded width in px, owned by the parent so it survives collapses. */
  width: number;
  onWidthChange: (width: number) => void;
  children: React.ReactNode;
}

/** Width of the thin bar the panel shrinks to when collapsed. */
const COLLAPSED_WIDTH = 34;
/** Bounds for the draggable panel width. */
const MIN_WIDTH = 200;
const MAX_WIDTH = 520;

/**
 * Docks a panel to the left or right of the canvas as a flex sibling, so it
 * narrows the canvas instead of overlapping it. The panel collapses to a slim
 * bar with an arrow to expand it again, and its inner edge can be dragged to
 * resize it.
 */
export const SidePanel: React.FC<SidePanelProps> = ({
  side,
  title,
  count,
  collapsed,
  onToggleCollapsed,
  width,
  onWidthChange,
  children
}) => {
  const border = side === 'left' ? { borderRight: '1px solid #ddd' } : { borderLeft: '1px solid #ddd' };

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault();
    const onMove = (e: MouseEvent) => {
      const raw = side === 'left' ? e.clientX : window.innerWidth - e.clientX;
      onWidthChange(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, raw)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const arrow = (expand: boolean): string => {
    const pointsRight = side === 'left' ? expand : !expand;
    return pointsRight ? '›' : '‹';
  };

  const arrowButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    fontSize: '20px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#636e72',
    padding: '4px 6px'
  };

  if (collapsed) {
    return (
      <div
        style={{
          width: `${COLLAPSED_WIDTH}px`,
          flex: `0 0 ${COLLAPSED_WIDTH}px`,
          backgroundColor: '#f8f9fa',
          ...border,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '8px',
          gap: '8px'
        }}
      >
        <button onClick={onToggleCollapsed} title={`Expand ${title}`} style={arrowButtonStyle}>
          {arrow(true)}
        </button>
        <span style={{ writingMode: 'vertical-rl', fontSize: '13px', color: '#636e72', userSelect: 'none' }}>
          {title}
        </span>
      </div>
    );
  }

  const resizeHandle = (
    <div
      onMouseDown={startResize}
      title="Drag to resize"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side === 'left' ? 'right' : 'left']: 0,
        width: '6px',
        cursor: 'col-resize',
        zIndex: 1
      }}
    />
  );

  return (
    <div
      style={{
        width: `${width}px`,
        flex: `0 0 ${width}px`,
        position: 'relative',
        backgroundColor: 'white',
        ...border,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      {resizeHandle}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
        <strong style={{ fontSize: '15px', color: '#2d3436' }}>
          {title}{count !== undefined ? ` (${count})` : ''}
        </strong>
        <button onClick={onToggleCollapsed} title={`Collapse ${title}`} style={arrowButtonStyle}>
          {arrow(false)}
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
};
